import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Reminder } from '@common/models/reminder';
import { Clock, CheckCircle2, Edit, BellRing, User, MessageSquareText } from 'lucide-react';
import type { Node } from 'slate';
import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import {
    selectTestReminderOpen,
    selectTestReminderData,
    closeTestReminderDialog,
    openEditor
} from '@common/reducers/RemindersUIReducer';
import { DBReminderThunks } from '@common/reducers/DBServiceThunks';
import { useImageProxy } from '@common/context/ImageProxyContext';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { selectMergedContactMap } from '@common-reducers/WhatsAppReducer';
import { getWaChatContactDisplayName } from '@common/utils/display-name-utils';
import { fetchAndCacheChatNameThunk, fetchAndCacheProfilePicUrlsThunk } from '@extension-context-content/thunks/WhatsappThunks';

// Image component that uses the image proxy for extension compatibility
const ProxiedImage: React.FC<{ url: string }> = ({ url }) => {
    const { processUrl } = useImageProxy();
    const [imgSrc, setImgSrc] = useState<string>(url);

    useEffect(() => {
        if (url) {
            processUrl(url).then(setImgSrc).catch(() => setImgSrc(url));
        }
    }, [url, processUrl]);

    return (
        <img
            src={imgSrc}
            alt="Reminder attachment"
            className="w-full h-auto max-h-[300px] object-contain bg-slate-100 dark:bg-slate-800"
        />
    );
};

const renderSlateContent = (nodes: any[]) => {
    return nodes.map((node, i) => {
        if (node.type === 'paragraph') {
            return (
                <p key={i} className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed whitespace-pre-wrap mb-2">
                    {node.children.map((child: any, j: number) => {
                        let text = child.text;
                        if (child.bold) return <strong key={j}>{text}</strong>;
                        if (child.italic) return <em key={j}>{text}</em>;
                        return <span key={j}>{text}</span>;
                    })}
                </p>
            );
        }
        if (node.type === 'image') {
            return (
                <div key={i} className="rounded-lg overflow-hidden my-3 border border-slate-200 dark:border-slate-700">
                    <ProxiedImage url={node.url} />
                </div>
            );
        }
        return null;
    });
};

export const TestReminderDialog: React.FC = () => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const [showButtonGlow, setShowButtonGlow] = useState(false);

    // Get state from Redux
    const open = useAppSelector(selectTestReminderOpen);
    const reminder = useAppSelector(selectTestReminderData);
    const mergedContactMap = useAppSelector(selectMergedContactMap);

    const relatedChatId = reminder?.relatedChatId;
    const cachedContact = relatedChatId ? mergedContactMap[relatedChatId] : undefined;
    const resolvedFromMap = getWaChatContactDisplayName(cachedContact, { fallback: '' });
    const isUnknownPlaceholder = !!reminder && (
        !reminder.relatedChatDisplayName ||
        reminder.relatedChatDisplayName === 'Unknown Contact' ||
        reminder.relatedChatDisplayName === relatedChatId
    );
    const chatDisplayName = isUnknownPlaceholder
        ? (resolvedFromMap || reminder?.relatedChatDisplayName || relatedChatId?.split('@')[0])
        : reminder?.relatedChatDisplayName;

    const cachedExt = cachedContact as { imgUrl?: string; profilePicLastUpdate?: string } | undefined;
    const cachedImgUrl = cachedExt?.imgUrl;
    const cachedPicLastUpdate = cachedExt?.profilePicLastUpdate;
    useEffect(() => {
        if (!relatedChatId) return;
        if (!cachedContact?.name) dispatch(fetchAndCacheChatNameThunk(relatedChatId));
        if (!cachedImgUrl && !cachedPicLastUpdate) {
            dispatch(fetchAndCacheProfilePicUrlsThunk(relatedChatId));
        }
    }, [dispatch, relatedChatId, cachedContact?.name, cachedImgUrl, cachedPicLastUpdate]);

    const handleClose = () => {
        dispatch(closeTestReminderDialog());
    };

    const handleClickOutside = (e: Event) => {
        e.preventDefault();
        // Trigger the glow animation
        setShowButtonGlow(true);
        // Reset after animation completes (600ms)
        setTimeout(() => setShowButtonGlow(false), 600);
    };

    const handleSnooze = () => {
        if (!reminder) return;
        const newDue = Date.now() + 3600000;
        dispatch(DBReminderThunks.patch({
            entity: { ...reminder, dueAt: newDue },
            options: { ignoreOriginalEntity: false }
        }));
        handleClose();
    };

    const handleComplete = () => {
        if (!reminder) return;
        dispatch(DBReminderThunks.patch({
            entity: { ...reminder, isActive: false },
            options: { ignoreOriginalEntity: false }
        }));
        handleClose();
    };

    const handleEdit = () => {
        if (!reminder) return;
        handleClose();
        dispatch(openEditor({ reminder }));
    };

    if (!reminder) return null;

    // Determine Related To Icon
    let RelatedIcon = User;
    if (reminder.relatedMessageId || reminder.relatedMessageText) {
        RelatedIcon = MessageSquareText;
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                className="sm:max-w-[425px] border-none shadow-2xl bg-white dark:bg-slate-950 rounded-2xl overflow-hidden p-0 [&>button]:hidden"
                onPointerDownOutside={handleClickOutside}
                onInteractOutside={handleClickOutside}
                style={{ zIndex: 99999999, pointerEvents: "auto" }}
            >
                {/* Header Decoration */}
                <div className="h-2 w-full" style={{ background: 'var(--fab-bg)' }} />

                <DialogHeader className="px-8 pt-8 pb-4">
                    <div className="flex items-center gap-2 mb-2">
                        <BellRing className="w-5 h-5 animate-pulse" style={{ color: '#259ae9' }} />
                        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#259ae9' }}>
                            {t('reminders.reminderDueTitle')}
                        </span>
                    </div>
                    <DialogTitle className="text-3xl font-extrabold text-slate-900 dark:text-white capitalize leading-tight">
                        {t('reminders.dontForgetTo')}
                    </DialogTitle>
                </DialogHeader>

                <div className="px-8 py-2 space-y-4 max-h-[60vh] overflow-y-auto">
                    <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-5 border border-slate-100 dark:border-slate-800 shadow-sm">
                        <h3 className="font-bold text-xl text-slate-900 dark:text-slate-100 !m-0 mb-2">
                            {reminder.title}
                        </h3>

                        {reminder.description && Array.isArray(reminder.description) && (
                            <div className="reminder-content text-slate-600 dark:text-slate-400">
                                {renderSlateContent(reminder.description)}
                            </div>
                        )}

                        {chatDisplayName && (
                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex flex-col gap-1.5">
                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    {t('reminders.relatedTo')}
                                </span>
                                <span className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#259ae9]/10 text-[#259ae9] dark:bg-[#259ae9]/20">
                                        <RelatedIcon className="w-3.5 h-3.5" />
                                    </span>
                                    {chatDisplayName}
                                </span>
                                {reminder.relatedMessageText && (
                                    <div className="mt-1 pl-3 border-l-2 border-slate-300 dark:border-slate-700">
                                        <p className="text-xs text-slate-500 italic line-clamp-2">
                                            "{reminder.relatedMessageText}"
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="px-8 pb-8 pt-4 flex flex-col sm:flex-col gap-3">
                    {/* Glow animation styles */}
                    <style>{`
                        @keyframes button-glow-pulse {
                            0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
                            50% { box-shadow: 0 0 8px 2px rgba(239, 68, 68, 0.5); }
                            100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
                        }
                        .button-glow {
                            animation: button-glow-pulse 0.6s ease-out;
                        }
                    `}</style>
                    <Button
                        onClick={handleComplete}
                        className={`w-full text-white rounded-xl h-12 text-base font-bold shadow-lg transition-all hover:scale-[1.01] active:scale-[0.99] hover:opacity-90 ${showButtonGlow ? 'button-glow' : ''}`}
                        style={{ background: 'var(--fab-bg)', color: 'var(--fab-text)' }}
                    >
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        {t('reminders.gotItThanks')}
                    </Button>

                    <div className="grid grid-cols-2 gap-3 w-full mt-2">
                        <Button
                            variant="outline"
                            onClick={handleSnooze}
                            className={`w-full rounded-xl h-11 border-slate-200 text-slate-600 hover:text-[#259ae9] hover:border-blue-100 hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors font-medium ${showButtonGlow ? 'button-glow' : ''}`}
                        >
                            <Clock className="w-4 h-4 mr-2" />
                            {t('reminders.inOneHour')}
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={handleEdit}
                            className={`w-full rounded-xl h-11 text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors font-medium ${showButtonGlow ? 'button-glow' : ''}`}
                        >
                            <Edit className="w-4 h-4 mr-2" />
                            {t('reminders.editAction')}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
